'use client'

/**
 * SearchableSelect / SearchableMultiSelect
 *
 * Headless-ish core for controlled, form-friendly pickers.
 *
 * SearchableSelect — single-select mode. Owns the trigger button, popover,
 * search field (auto-focused on open), filtering, click-outside,
 * Escape-to-close, arrow-key navigation, and the clear option.
 *
 * SearchableMultiSelect — multi-select mode. Same UX as above but keeps the
 * popover open on selection, shows a checkbox per row, and exposes selected
 * count in the trigger with a "Clear all" option. It lives in
 * `./searchable-multi-select` since #13473 and is re-exported below, so this
 * module is still the import site for both.
 *
 * Callers supply the item shape and render the option content, so each
 * concrete picker (PersonPicker, PersonMultiPicker, …) stays a thin typed
 * wrapper.
 *
 * Pure input: reports selection via `value` / `onChange`, never persists.
 *
 * ## Async / server-search mode (`onSearch` provided)
 *
 * When `onSearch` is passed, the component operates in async mode:
 * - Client-side filtering is bypassed.
 * - `onSearch(query)` is called on every keystroke; the caller debounces and
 *   fetches results, passing them back via `items`.
 * - While `isSearchLoading={true}`, the option rows are suppressed and a
 *   "Searching…" live region renders BELOW the list rather than inside it
 *   (#13479 — see "Where `role="listbox"` lives").
 * - A `renderFooter` render prop can supply an action row (e.g. "Unlink")
 *   below the results without requiring deferred mode.
 *
 * ## Deferred-commit mode (`deferred={true}`)
 *
 * When `deferred` is true the component stages the selection locally inside
 * the popover instead of calling `onChange` on every click.  The committed
 * value is only propagated when the popover closes:
 *
 * - Click-outside → implicit commit: `onCommit(stagedValue)` fires if the
 *   staged value differs from `value`.
 * - Escape key → implicit commit: same as click-outside.
 * - Explicit action via `renderFooter` → caller calls the `commit` or
 *   `cancel` helpers supplied to the render prop.
 *
 * The `renderFooter` render prop receives `(staged, commit, cancel)` and can
 * render Save/Cancel/Done buttons with any additional UI (e.g. isSaving
 * state, "1 selected" label) without requiring that logic inside this file.
 *
 * ## `@sai/ui` (#13211, Stage 4 slice 9 of #13197)
 *
 * The popover's search field IS the package's `SearchInput`; everything around
 * it — trigger, portaled listbox, keyboard model, deferred commit, footer — is
 * still this file's. That split is measured, not unfinished.
 *
 * The field moved because it was a hand-rolled `<input type="text">` with its
 * own chrome and NO accessible name — two `anti-patterns.mdx` entries at once
 * ("Component Reuse: Re-implementing SearchInput", "Input: Empty Aria Label").
 * `SearchInput` brings the magnifier, the package's chrome, and an `ariaLabel`
 * from the caller's `searchPlaceholder`. `hideClear` suppresses its clear
 * button: the field never had one, and these suites address the trigger as
 * `getByRole('button')` WHILE OPEN, so a second button would break them.
 *
 * The control itself did NOT become `Combobox` / `MultiSelect`. Checked
 * against `@sai/ui@1.35.0`, not assumed:
 *
 *   - `MultiSelect` is `@deprecated` in the package ("Use Combobox for
 *     multi-select controls instead") and takes
 *     `options: { label: string; value: string }[]`, which cannot express this
 *     component's `renderOption: (item: T) => ReactNode` — avatars, tinted
 *     rows, badges. Adopting it would push a prop-shape change onto callers,
 *     the one thing Stage 4's adapter pattern exists to prevent.
 *   - `Combobox` has no counterpart for `deferred`/`onCommit`, `renderFooter`,
 *     `allowClear`+`clearLabel`, `isSearchLoading`, `defaultOpen` with
 *     mount-symmetric `onOpenChange`, or `selectedClassName`. Its keyboard
 *     model differs observably from the one this file's three suites assert
 *     (it activates the first option on open, and marks the active row with
 *     `bg-action-primary` rather than `ring-2`), and it closes on Escape
 *     through Radix's dismissable layer, which does NOT stop the event
 *     reaching `useModalEscape`'s document listener — the #12636 regression
 *     where one Escape also discarded the user's unsaved modal edits.
 *
 * So the trigger stays a plain `<button aria-haspopup="listbox">` and the prop
 * signature above is unchanged. Adopting the package's compound control is a
 * behaviour change needing its own issue and its own call-site migration —
 * not a quiet simplification of this file.
 *
 * ## Grouped mode (`getGroup`, #13473)
 *
 * `getGroup` is opt-in and changes NOTHING for a caller that omits it: the
 * nine existing call sites render byte-identical markup, because the grouped
 * branch is not entered at all. When it IS supplied, `filtered` is stable
 * sorted by group — after the text filter, so an empty group simply has no
 * run and therefore no heading — and a heading is emitted wherever the group
 * changes between two consecutive entries of that still-FLAT array.
 *
 * Flat is the load-bearing word. `focusedIndex` indexes straight into
 * `filtered`, the option ids are `optionRowId(listboxId, idx)` over that same
 * index (`./searchable-select-row-ids`, #13504), and `aria-activedescendant`
 * is derived from it too. Because a heading is
 * never a member of `filtered`, it cannot enter the arrow-key path, cannot be
 * matched by the filter, and cannot shift an option's id — by construction
 * rather than by a guard someone has to remember. `groupOrder` sets heading
 * order; groups it does not name fall to the end in first-appearance order.
 *
 * ### The `aria-required-children` decision, made rather than inherited
 *
 * Each run is wrapped in `role="group"` with an `aria-label` of the group
 * name, and the visible heading inside it is `aria-hidden` so the region is
 * announced once, by the group, rather than twice. `group` is what ARIA 1.2
 * sanctions between `listbox` and `option`, so the owned-children set stays
 * legal and an option's own accessible name is not made to carry its region.
 *
 * The alternative considered was `role="presentation"` headings, leaving each
 * option's name to carry its own region. Rejected: it is only equivalent when
 * the caller's `renderOption` happens to repeat the region (true for IANA zone
 * ids, false in general), and it drops the region entirely for a user arrowing
 * through the list. Both shapes were MEASURED with `@axe-core/playwright`
 * against this exact subtree before choosing — neither adds a violation, so
 * the choice was made on what a screen reader announces, not on what axe
 * tolerates.
 *
 * What that measurement ALSO showed was a pair of violations that predated
 * grouping and had nothing to do with it, both on the element then carrying
 * `role="listbox"`: `aria-required-children` (critical) and
 * `aria-input-field-name` (serious). They are fixed — see the next section —
 * and `tests/e2e/critical-path/golden-path-searchable-select-grouped-13473.spec.ts`
 * asserts ZERO on the open popover rather than pinning them as a ceiling.
 *
 * ## Where `role="listbox"` lives, and what is deliberately outside it (#13479)
 *
 * The role and the `listboxId` the trigger's `aria-controls` resolves to both
 * sit on the **scrolling option list**. They used to sit on the portal root,
 * which also owns the search field and the `renderFooter` row — so the
 * listbox owned a `textbox` (`aria-required-children`, critical) and had no
 * accessible name (`aria-input-field-name`, serious).
 * `src/components/ui/select.tsx` went the same way at #12796 / #13186 and is
 * the pattern this copies. `aria-activedescendant` did NOT come with them —
 * see item 4.
 *
 * Four consequences worth stating, because each is a thing a later edit
 * could undo without any local symptom:
 *
 *  1. **The name is the trigger's label STRING, copied.** Not
 *     `aria-labelledby={triggerId}`: that reference resolves but computes an
 *     EMPTY name in Chromium, because accname recurses into the embedded
 *     control it points at and takes its VALUE instead of its label (#13186
 *     measured exactly that on `select.tsx`). axe does not implement the
 *     carve-out and reports such a panel as named, so only a real-browser
 *     assertion sees it. `ariaLabel` is what the caller named the trigger
 *     with; `placeholder` is the trigger's own content when nothing is
 *     selected, which is why it is the fallback rather than a literal.
 *  2. **The search field and the footer are siblings of the listbox**, inside
 *     the portal root. That root carries `data-searchable-select-popover` so
 *     a test can still address the whole popover: two e2e specs reached the
 *     footer's Cancel button and the search field through
 *     `getByRole('listbox')` before this, and now use that marker
 *     (`critical-path/golden-path-dashboard-review-captain-12298.spec.ts`,
 *     `journeys/groups/aor-edit-pickers.spec.ts`).
 *  3. **The async "Searching…" live region is outside the listbox too.**
 *     `aria-live` is a GLOBAL aria attribute, so axe counts that div as an
 *     owned child of whatever element it sits in — inside the listbox it is
 *     another `aria-required-children` failure, reachable only in async mode
 *     and therefore only from a caller no component-test page renders. It is
 *     also mounted for the whole life of an async popover and EMPTIED rather
 *     than unmounted, because a region that appears together with its own
 *     text is frequently never announced; only its padding toggles, since
 *     `display: none` would take it out of the accessibility tree entirely.
 *  4. **`aria-activedescendant` is on the SEARCH FIELD, not on the listbox.**
 *     The search field is what holds DOM focus while the popover is open,
 *     and ARIA honours `aria-activedescendant` only on the focused element —
 *     so on the option list it would be inert, and the arrow-key walk this
 *     component advertises would still be silent to a screen reader. The
 *     field therefore carries `role="combobox"` with `aria-expanded` and
 *     `aria-controls={listboxId}`; the option ids it points at live in the
 *     `aria-controls` target, which is where ARIA requires them. Those ids
 *     are derived FROM `listboxId` since #13504 — see
 *     `./searchable-select-row-ids` — because `getElementById` returns the
 *     first match, so a document-global row id pointed this attribute at
 *     another popover's row whenever two were open. #13479's
 *     acceptance criterion 1 asked for this attribute on the option list
 *     alongside the role, and that half of it was wrong for the same reason
 *     the portal root was: correctness here is about what has focus, not
 *     about which element carries the role. Note that `select.tsx`, the
 *     precedent for items 1-2, does not use `aria-activedescendant` at all —
 *     Radix moves real focus into its panel instead — so this item is not
 *     copied from it.
 *
 * Structure is pinned by
 * `tests/unit/components/searchable-select-listbox-scope-13479.test.tsx`; the
 * zero-violation claim itself is e2e, since axe does not exist in the unit
 * tier.
 *
 * ### `SearchableMultiSelect` is explicitly OUT OF SCOPE for grouping
 *
 * It has its own index space — the same `optionRowId` helper under its own
 * `listboxId` — and no caller needs headings. Adding `getGroup` there would
 * duplicate the run logic against a second index space for a hypothetical
 * consumer; do it when one exists, and share `sortByGroup` / `toGroupRuns`
 * below when you do. The same statement is repeated at the top of
 * `./searchable-multi-select`, which is where someone about to add it would
 * be reading.
 *
 * The magnifier is a sai-ui `Icon`, which resolves its path data through a
 * dynamic `import()` (`dist/lib/get-icon-data.js`) and renders `null` until
 * that lands — so it paints a tick after the field, which opens and
 * auto-focuses in the same frame. That is the package's loading strategy, not
 * a race in this file: do not "fix" it here. On a failed resolve the package
 * logs to `console.error` and renders nothing, so a magnifier that never
 * appears is a package or network symptom rather than a mistake at this call
 * site.
 */

import { SearchInput } from '@sai/ui/components'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from '@/components/icons'
import { useClickOutside } from '@/hooks/use-click-outside'
import { ATTENDEE_FIRST_PICKER } from '@/lib/meetings/attendee-first-ranking'
import { cn } from '@/lib/utils'
import { AttendeeFirstHint } from './attendee-first-hint'
import { attendeeFirstView } from './searchable-select-attendee-first'
import {
  listboxAccessibleName,
  SEARCHABLE_SELECT_POPOVER_ATTR,
} from './searchable-select-listbox-name'
import { type MenuPos, measureMenuPos } from './searchable-select-menu-pos'
import {
  clearRowId,
  findRowIn,
  focusedRowId,
  optionRowId,
} from './searchable-select-row-ids'
import { SEARCH_FIELD_CLASSES } from './searchable-select-search-field'
import * as triggerName from './searchable-select-trigger-name'

/**
 * `SearchableMultiSelect` moved to its own module when grouped mode pushed
 * this file past the 1000-line `check:file-size-ratchet` cap (#13473). It is
 * re-exported here because THIS module is the pair's public entry point:
 * nine callers and four test suites import both names from it, and a split
 * made for a line count has no business rewriting their import paths.
 */
export { SearchableMultiSelect } from './searchable-multi-select'

/**
 * One run of consecutive same-group options inside the flat filtered array.
 *
 * `startIndex` is the run's offset INTO `filtered`, which is what keeps the
 * option ids (`optionRowId(listboxId, idx)`) and `focusedIndex` addressing
 * the same positions they would have addressed without grouping.
 */
type GroupRun<T> = { group: string; startIndex: number; items: T[] }

/**
 * Stable sort `rows` so same-group entries are adjacent (#13473).
 *
 * Rank comes from `groupOrder` where it names a group, and otherwise from the
 * order in which `rows` first mentions that group — appended AFTER every
 * named one, so an unlisted group can never displace a listed one. Ties keep
 * the caller's original order: the explicit index tiebreak says so rather
 * than leaning on `Array#sort` being specified as stable, because the
 * within-group order is a documented property here and not an accident.
 *
 * A group named in `groupOrder` that no row belongs to contributes no run,
 * so it renders nothing — an empty group is absent, not an empty heading.
 *
 * A REPEATED name in `groupOrder` keeps its FIRST position and the ranks stay
 * dense. Built from the raw index instead, a duplicate does two things at
 * once: the later entry overwrites the earlier one in `declared`, and
 * `declared.size` then undercounts the array, so `declared.size + firstSeen`
 * lands undeclared groups on top of declared ones. `['A','B','A']` over rows
 * in A/C/A/B/C order produced runs `B A C A C` — non-adjacent same-group runs,
 * which is the one invariant this function's name promises.
 */
function sortByGroup<T>(
  rows: T[],
  getGroup: (item: T) => string,
  groupOrder?: string[],
): T[] {
  const declared = new Map<string, number>()
  for (const group of groupOrder ?? []) {
    if (!declared.has(group)) declared.set(group, declared.size)
  }
  const firstSeen = new Map<string, number>()
  for (const row of rows) {
    const group = getGroup(row)
    if (!declared.has(group) && !firstSeen.has(group)) {
      firstSeen.set(group, firstSeen.size)
    }
  }
  const rank = (group: string): number =>
    declared.get(group) ?? declared.size + (firstSeen.get(group) ?? 0)

  return rows
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        rank(getGroup(a.item)) - rank(getGroup(b.item)) || a.index - b.index,
    )
    .map(entry => entry.item)
}

/** Split an already group-sorted array into its runs, keeping flat offsets. */
function toGroupRuns<T>(rows: T[], getGroup: (item: T) => string) {
  const runs: GroupRun<T>[] = []
  rows.forEach((item, index) => {
    const group = getGroup(item)
    const current = runs[runs.length - 1]
    if (current && current.group === group) {
      current.items.push(item)
      return
    }
    runs.push({ group, startIndex: index, items: [item] })
  })
  return runs
}

export interface SearchableSelectProps<T> {
  /** Selected item id, or null when none is selected. */
  value: string | null
  /** Called with the new item id, or null when cleared. */
  onChange: (id: string | null) => void
  /** Items available for selection. */
  items: T[]
  /** Extract the stable id from an item. */
  getId: (item: T) => string
  /** Text matched against the search query (case-insensitive). */
  getSearchText: (item: T) => string
  /** Trigger content shown when an item is selected. */
  renderSelected: (item: T) => ReactNode
  /** Row content for an item in the option list. */
  renderOption: (item: T) => ReactNode
  /** Trigger text shown when nothing is selected. */
  placeholder?: string
  /** Placeholder for the search input. */
  searchPlaceholder?: string
  /** Label for the clear option. */
  clearLabel?: string
  /** Whether the clear option is shown. Defaults to true. */
  allowClear?: boolean
  /** Message shown when no items match the query. */
  emptyLabel?: string
  disabled?: boolean
  /** id applied to the trigger button (for label association). */
  id?: string
  /** Names the trigger, unless `ariaLabelledBy` is set; names the listbox. */
  ariaLabel?: string
  /** Visible label id; trigger named by it + its value (#16638). */
  ariaLabelledBy?: string
  /** `data-testid` applied to the trigger button, for addressable tests. */
  testId?: string
  // ---------------------------------------------------------------------------
  // Grouped mode (#13473) — see the "Grouped mode" section of this file's header
  // ---------------------------------------------------------------------------
  /**
   * When provided, options are grouped under non-selectable region headings.
   *
   * The returned key is both the heading text and the `role="group"` label.
   * Omit it and the component behaves exactly as it did before #13473 — no
   * headings, no group wrappers, caller order preserved.
   *
   * Headings are never focusable, never selectable and never matched by the
   * search query: they are rendered from the flat filtered array rather than
   * being members of it.
   *
   * WITH `onSearch` (async mode) GROUPING STILL APPLIES, and it reorders what
   * the caller returned. Async mode skips the local text filter, so `items`
   * arrives already ordered by whatever the server ranked it by — grouping
   * then sorts across that, and a relevance order does not survive it.
   * Stated rather than prevented: a caller that asks for headings has asked
   * for the list to be organised by region rather than by rank, and silently
   * dropping the grouping would be the more surprising of the two. A caller
   * that wants server order intact should not pass `getGroup`.
   *
   * WITH `deferred` it composes unchanged. Deferral decides *when* a
   * selection commits (`stagedValue` vs `value`); grouping decides the ORDER
   * of the flat array those indices address. Neither reads the other.
   */
  getGroup?: (item: T) => string
  /**
   * Heading order for `getGroup`. Groups named here come first, in this
   * order; groups not named fall to the end in first-appearance order. A
   * named group with no matching items renders nothing.
   *
   * Ignored when `getGroup` is absent.
   */
  groupOrder?: string[]
  /**
   * Attendees-first mode (#15938; ignored with `onSearch`): only these ids
   * under an empty query, then ranked first and grouped under a query. See
   * `./searchable-select-attendee-first`. A caller's `getGroup` wins.
   */
  prioritizeIds?: readonly string[] | null
  className?: string
  /**
   * Class applied to the focused/selected option row.
   * Defaults to `bg-status-blue-bg-light`. Pass the consumer's tint class
   * (e.g. MEASURABLE_TINT.bg) to match the surrounding surface.
   */
  selectedClassName?: string
  // ---------------------------------------------------------------------------
  // Async / server-search mode
  // ---------------------------------------------------------------------------
  /**
   * When provided, the component operates in **async mode**:
   * - Client-side filtering is bypassed; callers pass server-fetched `items`.
   * - Called on every keystroke with the current query value so the caller
   *   can debounce and fetch results as needed.
   */
  onSearch?: (query: string) => void
  /**
   * When `onSearch` is present, set this to `true` while results are being
   * fetched.  The option list will show a "Searching…" indicator instead of
   * the items / empty state.
   */
  isSearchLoading?: boolean
  // --- Deferred-commit mode ------------------------------------------------
  /**
   * When true, item clicks stage the selection locally instead of calling
   * `onChange` immediately.  The selection is committed (via `onCommit`) when
   * the popover closes (click-outside, Escape, or an explicit action from the
   * `renderFooter` render prop).  The existing `onChange` prop is unused while
   * deferred mode is active.
   */
  deferred?: boolean
  /**
   * Called with the final value when the popover closes in deferred mode.
   * Fires only when the staged value differs from the incoming `value` prop
   * on implicit close (click-outside / Escape).  When the caller triggers
   * close via `renderFooter`'s `commit` helper, it always fires.
   */
  onCommit?: (value: string | null) => void
  /**
   * When true, the popover opens immediately on mount (single-click UX).
   * Used by AorPicker to skip the two-step open sequence: clicking the
   * outer trigger mounts SearchableSelect with defaultOpen=true so the
   * search popover is visible after a single click.
   */
  defaultOpen?: boolean
  /**
   * Called when the popover open state changes. Allows the parent to react
   * to the picker closing (e.g. to unmount it and restore the trigger button).
   *
   * **Symmetric contract**: if the component is mounted with
   * `defaultOpen={true}`, `onOpenChange(true)` is fired once on mount so the
   * parent receives the same notification it would get from any subsequent
   * open/close cycle.
   *
   * The callback is NOT stable-identity-sensitive — the ref-latch inside the
   * component always reads the latest value, so you may pass an inline
   * function without causing extra effect reruns.
   *
   * Applies to `SearchableSelect` only; `SearchableMultiSelect` has no
   * `defaultOpen` / `onOpenChange` props.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Render prop for a custom footer inside the popover.
   *
   * In deferred mode: receives `(staged, commit, cancel)` helpers.
   * In async (non-deferred) mode: receives `(null, noop, noop)` — the
   * footer is rendered unconditionally whenever this prop is provided,
   * useful for e.g. an "Unlink" action row below the results list.
   *
   * @param staged   The currently-staged (uncommitted) value (deferred mode only).
   * @param commit   Call to persist `staged` and close the popover.
   * @param cancel   Call to discard changes and close the popover.
   */
  renderFooter?: (
    staged: string | null,
    commit: () => void,
    cancel: () => void,
  ) => ReactNode
}

export function SearchableSelect<T>({
  value,
  onChange,
  items,
  getId,
  getSearchText,
  renderSelected,
  renderOption,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  clearLabel = 'No selection',
  allowClear = true,
  emptyLabel = 'No matches found',
  disabled = false,
  id,
  ariaLabel,
  ariaLabelledBy,
  testId,
  className,
  selectedClassName = 'bg-status-blue-bg-light',
  getGroup,
  groupOrder,
  prioritizeIds = null,
  onSearch,
  isSearchLoading = false,
  deferred = false,
  onCommit,
  renderFooter,
  defaultOpen = false,
  onOpenChange,
}: SearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  // `aria-controls` needs a stable id for the portaled listbox so assistive
  // tech — and tests — can tie the dropdown to THIS trigger rather than to
  // whichever `role="listbox"` happens to be first in the document.
  const fallbackId = useId()
  const listboxId = `${id ?? fallbackId}-listbox`
  const valueId = triggerName.triggerValueId(id ?? fallbackId)

  const onOpenChangeRef = useRef(onOpenChange)
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange
  }, [onOpenChange])
  useEffect(() => {
    if (defaultOpen) onOpenChangeRef.current?.(true)
  }, [defaultOpen]) // fires once on mount when defaultOpen is true

  const [query, setQuery] = useState('')
  /**
   * Keyboard-focused row index. -1 = the "clear" row (when allowClear),
   * 0..N-1 = filtered items. null = no arrow-key focus active yet.
   */
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  /**
   * Deferred-commit mode: tracks the pending selection inside the popover.
   * Initialised from `value` each time the popover opens.
   */
  const [stagedValue, setStagedValue] = useState<string | null>(value)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Fixed-position coords for the portaled popover (see useLayoutEffect below).
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null)

  // Wrapper that sets isOpen and notifies the parent via onOpenChange.
  const setOpen = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )

  // Implicit close handler: commit staged value if it differs from committed value.
  const handleImplicitClose = useCallback(() => {
    if (deferred) {
      if (stagedValue !== value) {
        onCommit?.(stagedValue)
      }
    }
    setOpen(false)
  }, [deferred, stagedValue, value, onCommit, setOpen])

  // Close on click outside the trigger OR the (portaled) popover.
  useClickOutside([containerRef, dropdownRef], handleImplicitClose, isOpen)

  // The popover is portaled to <body> so an `overflow` ancestor (e.g. a modal's
  // scrollable body) can't clip it (#10298). Detached from the trigger in the
  // DOM, it is positioned with `fixed` coords from the trigger's rect, kept in
  // sync while open (scroll/resize, capture:true so any ancestor scroll counts).
  useLayoutEffect(() => {
    if (!isOpen) return
    const update = () => {
      const el = containerRef.current
      if (!el) return
      setMenuPos(measureMenuPos(el))
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen])

  // On open: reset keyboard focus state and sync staged value. In async mode,
  // also trigger an initial search with the empty query so the caller can
  // load the initial result set (query is always '' on open because `open()`
  // resets it before setting isOpen).
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(null)
      if (deferred) {
        // Reset staged value to the committed value each time the popover opens.
        setStagedValue(value)
      }
      if (onSearch) {
        // Fire onSearch('') on every open so the caller can load the initial
        // result set. This is intentional in async mode — the caller debounces
        // and the empty query populates the list before the user types.
        onSearch('')
      }
    }
  }, [isOpen, deferred, value, onSearch])

  // Focus the search field once the portaled popover exists (it waits on
  // `menuPos`); on `defaultOpen` it did not yet when isOpen was set (#15969).
  const menuShown = menuPos !== null
  useEffect(() => {
    if (isOpen && menuShown) searchRef.current?.focus()
  }, [isOpen, menuShown])
  // Reset keyboard focus when the query changes so the user doesn't end up
  // on a stale index after the filtered list shrinks.
  useEffect(() => {
    setFocusedIndex(null)
  }, [query])

  // The "active" value for highlighting rows depends on the mode:
  // - deferred mode: use stagedValue (pending local selection)
  // - normal mode: use value (committed)
  const activeValue = deferred ? stagedValue : value

  // For the trigger display, always reflect the committed value (not staged).
  const triggerSelected = items.find(item => getId(item) === value) ?? null

  // Async mode: skip client-side filtering; items are provided by the caller.
  // Sync mode: apply the usual client-side text filter.
  const asyncMode = onSearch !== undefined
  const q = query.toLowerCase()
  const attendeeView =
    asyncMode || prioritizeIds === null
      ? null
      : attendeeFirstView(items, prioritizeIds, getId, getSearchText, query)
  const matched = asyncMode
    ? items
    : (attendeeView?.rows ??
      items.filter(item => getSearchText(item).toLowerCase().includes(q)))
  const groupBy = getGroup ?? attendeeView?.getGroup

  // Grouped mode reorders the SURVIVORS of the filter, never the filter's
  // input: a group whose every option was filtered away has no run left and
  // so renders no heading. Without `getGroup` this is the same array object
  // the flat path has always rendered.
  const filtered = groupBy ? sortByGroup(matched, groupBy, groupOrder) : matched
  const groupRuns = groupBy ? toGroupRuns(filtered, groupBy) : null

  // Total rows navigable via arrow keys: optional clear row + filtered items.
  const clearRowIndex = allowClear ? -1 : null
  const firstItemIndex = 0
  const lastItemIndex = filtered.length - 1
  const minIndex = allowClear ? -1 : 0
  const maxIndex = lastItemIndex

  const open = () => {
    if (disabled) return
    setQuery('')
    // Compute popover position synchronously so the portal mounts on the same
    // render isOpen flips — else the search-input auto-focus fires before the
    // (portaled) input exists (#10298).
    const el = containerRef.current
    if (el) setMenuPos(measureMenuPos(el))
    setOpen(true)
  }

  // Explicit commit: persist staged value and close.
  const commitDeferred = useCallback(() => {
    onCommit?.(stagedValue)
    setOpen(false)
    triggerRef.current?.focus()
  }, [onCommit, stagedValue, setOpen])

  // Cancel: discard staged changes and close without committing.
  const cancelDeferred = useCallback(() => {
    setStagedValue(value)
    setOpen(false)
    triggerRef.current?.focus()
  }, [value, setOpen])

  const closeToTrigger = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const select = (itemId: string | null) => {
    if (deferred) {
      // Stage the selection locally; do not fire onChange or close the popover.
      setStagedValue(itemId)
    } else {
      // Closing unmounts the focused row or field: refocus the trigger (#16644).
      triggerRef.current?.focus()
      onChange(itemId)
      setOpen(false)
    }
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      // Escape closes THIS dropdown only. Without stopPropagation the event
      // continues to the document-level listener `useModalEscape` installs,
      // which closes the surrounding Modal too — one keypress discarded the
      // user's unsaved edits (#12636).
      e.stopPropagation()
      handleImplicitClose()
      triggerRef.current?.focus()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (filtered.length === 0 && !allowClear) return
      setFocusedIndex(prev => {
        if (prev === null) return allowClear ? clearRowIndex! : firstItemIndex
        return Math.min(prev + 1, maxIndex)
      })
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (filtered.length === 0 && !allowClear) return
      setFocusedIndex(prev => {
        if (prev === null) return maxIndex
        return Math.max(prev - 1, minIndex)
      })
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex === null) return
      if (focusedIndex === -1) return select(null)
      const item = filtered[focusedIndex]
      if (item) select(getId(item))
    }
  }

  // Scroll focused option into view when arrow-key focus changes.
  useEffect(() => {
    if (!listboxRef.current) return
    const focusedId = focusedRowId(listboxId, focusedIndex)
    if (!focusedId) return
    // Not `querySelector`: the prefix carries the caller's unvetted `id`
    // prop, and every selector form breaks on some legal value of it.
    // `findRowIn` resolves by `getElementById` and checks containment — see
    // that module.
    const el = findRowIn(listboxRef.current, focusedId)
    el?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex, listboxId])

  // Instance-scoped (#13504), and derived from the same two functions the
  // rows below are, so the attribute cannot drift from the ids it names.
  const activedescendant = focusedRowId(listboxId, focusedIndex)

  // `role="listbox"` requires an accessible name. See item 1 of this file's
  // "Where `role=\"listbox\"` lives" section for why this is the trigger's
  // label as a STRING and not an `aria-labelledby` pointing at the trigger.
  const listboxLabel = listboxAccessibleName(ariaLabel, placeholder)

  // Async mode's loading indicator carries `aria-live`, a global aria
  // attribute, which would make it an owned child of the listbox. It renders
  // as a SIBLING of the option list instead (item 3 of that same section).
  const isSearching = asyncMode && isSearchLoading

  /**
   * One option row, addressed by its index into the flat `filtered` array.
   *
   * Both the flat and the grouped branches render through this, so grouped
   * mode cannot drift from the markup nine existing callers depend on — the
   * only difference between the two branches is what wraps the rows.
   */
  const renderOptionRow = (item: T, idx: number) => {
    const itemId = getId(item)
    const isSelected = itemId === activeValue
    const isFocused = focusedIndex === idx
    return (
      <button
        key={itemId}
        id={optionRowId(listboxId, idx)}
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={() => select(itemId)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md',
          isSelected ? selectedClassName : 'hover:bg-fill-10',
          isFocused && 'outline-none ring-2 ring-stroke-selected',
        )}
      >
        {renderOption(item)}
      </button>
    )
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        {...triggerName.triggerNameProps(ariaLabel, ariaLabelledBy, valueId)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        data-testid={testId}
        disabled={disabled}
        onClick={() => (isOpen ? handleImplicitClose() : open())}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'border border-stroke-light rounded-lg bg-surface-elevated',
          'focus:outline-none focus:ring-2 focus:ring-stroke-selected',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <triggerName.TriggerValue id={valueId} placeholder={placeholder}>
          {triggerSelected ? renderSelected(triggerSelected) : null}
        </triggerName.TriggerValue>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-secondary transition-transform flex-shrink-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen &&
        !disabled &&
        menuPos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            // The popover SHELL — no role of its own, because it owns the
            // search field and the footer, neither of which a listbox may
            // own (#13479). The marker is the addressable hook that replaced
            // `getByRole('listbox')` in the two specs that reached those two
            // elements through it.
            {...{ [SEARCHABLE_SELECT_POPOVER_ATTR]: '' }}
            data-testid={
              attendeeView ? ATTENDEE_FIRST_PICKER.testId : undefined
            }
            tabIndex={-1}
            // `z-menu`: above any modal or drawer (#16990); fixed coords keep an overflow
            // ancestor from clipping it (#10298); flips, clamps + clips, so a
            // flush footer's focus ring is drawn inset (#16177).
            style={{
              position: 'fixed',
              top: menuPos.top,
              bottom: menuPos.bottom,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
            }}
            data-placement={menuPos.placement}
            className="z-menu flex flex-col overflow-hidden rounded-lg border border-stroke-regular bg-surface-elevated shadow-md"
          >
            <div className="p-2 border-b border-stroke-regular">
              <SearchInput
                ref={searchRef}
                ariaLabel={searchPlaceholder}
                // The COMBOBOX, because this is the element that holds DOM
                // focus while the popover is open (the effect above focuses
                // it). `aria-activedescendant` is only honoured on the
                // focused element, so it belongs here and NOT on the option
                // list — see item 4 of this file's "Where `role=\"listbox\"`
                // lives" section. sai-ui forwards these through `Input`'s
                // rest spread onto the `<input>`; only the camelCase
                // `ariaLabel` family is intercepted.
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={activedescendant}
                hideClear
                size="sm"
                value={query}
                placeholder={searchPlaceholder}
                onChange={next => {
                  setQuery(next)
                  if (asyncMode) onSearch(next)
                }}
                onKeyDown={handleSearchKeyDown}
                className={SEARCH_FIELD_CLASSES}
              />
            </div>
            <div
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              aria-label={listboxLabel}
              // No `aria-activedescendant` here on purpose: it lives on the
              // search field, which is the element that actually has focus.
              className="max-h-60 min-h-0 overflow-y-auto p-1"
            >
              {allowClear && (
                <button
                  id={clearRowId(listboxId)}
                  type="button"
                  role="option"
                  aria-selected={activeValue === null}
                  onClick={() => select(null)}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm rounded-md text-text-secondary',
                    focusedIndex === clearRowIndex
                      ? 'bg-fill-10 outline-none ring-2 ring-stroke-selected'
                      : 'hover:bg-fill-10',
                  )}
                >
                  {clearLabel}
                </button>
              )}
              {!isSearching && (
                <>
                  {groupRuns
                    ? groupRuns.map(run => (
                        // `role="group"` + `aria-label`, with the visible
                        // heading `aria-hidden`, is the recorded
                        // aria-required-children decision — see this file's
                        // header. The wrapper holds no index of its own: its
                        // rows keep their offsets into the flat array.
                        // Keyed by `startIndex`, not by `group`: the offset is
                        // unique per run by construction, where a group name
                        // is only unique while `sortByGroup` holds its
                        // adjacency invariant — and a React key is the wrong
                        // place to depend on another function's invariant.
                        <div
                          key={run.startIndex}
                          role="group"
                          aria-label={run.group}
                        >
                          <div
                            aria-hidden="true"
                            data-testid="searchable-select-group-heading"
                            className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-text-secondary"
                          >
                            {run.group}
                          </div>
                          {run.items.map((item, offset) =>
                            renderOptionRow(item, run.startIndex + offset),
                          )}
                        </div>
                      ))
                    : filtered.map((item, idx) => renderOptionRow(item, idx))}
                  {filtered.length === 0 && !attendeeView?.showHint && (
                    <div className="px-3 py-4 text-center text-sm text-text-secondary">
                      {emptyLabel}
                    </div>
                  )}
                </>
              )}
            </div>
            {attendeeView?.showHint && <AttendeeFirstHint />}
            {asyncMode && (
              // Outside the listbox on purpose — `aria-live` is global, so
              // inside it this div is an unallowed owned child (#13479).
              //
              // Mounted for the whole life of an async popover and emptied
              // rather than unmounted: a screen reader announces a change
              // WITHIN a region it was already observing, so a region that
              // appears together with its own text is frequently not
              // announced at all. `asyncMode` is the caller's own switch, so
              // a non-async popover still renders nothing.
              //
              // The PADDING is what toggles — not the element, and not
              // `display`. A `hidden` / `display: none` live region is out of
              // the accessibility tree and announces nothing, which is the
              // same defect one step over; an empty div with no padding is
              // zero height and invisible while staying observed.
              <div
                aria-live="polite"
                className={cn(
                  'text-center text-sm text-text-secondary',
                  isSearching && 'px-3 py-4',
                )}
              >
                {isSearching ? 'Searching…' : ''}
              </div>
            )}
            {renderFooter && (
              <div className="border-t border-stroke-regular [&_:focus-visible]:ring-inset">
                {deferred
                  ? renderFooter(stagedValue, commitDeferred, cancelDeferred)
                  : renderFooter(null, closeToTrigger, closeToTrigger)}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
